import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Calculator, Bike, Footprints } from 'lucide-react';
import { calculateZones } from '../utils/calculateZones';
import ZoneCard from './ZoneCard';

const getMethodDisplayInfo = (method, sport) => {
  switch (method) {
    case 'lab':
      return {
        name: 'Lab Test (3-Zone Model)',
        accuracy: 'Gold Standard 🥇',
        description: 'Uses your specific Ventilatory Thresholds (VT1/VT2) or Lactate Thresholds (LT1/LT2) from a metabolic cart to define the three physiological intensity domains.',
        audience: 'Professional or serious athletes with access to physiological testing.',
        formula: '3-Zone Physiological Model (Moderate, Heavy, Severe)',
        accuracyBg: 'bg-yellow-100 text-yellow-800'
      };
    case 'friel':
      return {
        name: `Joe Friel LTHR (${sport === 'cycling' ? 'Cycling' : 'Running'})`,
        accuracy: 'Silver Standard 🥈',
        description: 'Calculates 7 zones based on your Lactate Threshold Heart Rate (LTHR), with sport-specific percentages for Running and Cycling.',
        audience: 'Competitive athletes who perform regular field tests.',
        formula: `Based on 30-min Time Trial Average HR for ${sport === 'cycling' ? 'Cycling' : 'Running'}`,
        accuracyBg: 'bg-gray-200 text-gray-800'
      };
    case 'karvonen':
      return {
        name: 'Karvonen (Heart Rate Reserve)',
        accuracy: 'Bronze Standard 🥉',
        description: 'Accounts for individual fitness levels by incorporating resting heart rate.',
        audience: 'Intermediate runners looking for more personalized zones than age-based formulas.',
        formula: '((Max HR - Resting HR) × %) + Resting HR',
        accuracyBg: 'bg-orange-100 text-orange-800'
      };
    case 'tanaka':
      return {
        name: 'Tanaka Formula',
        accuracy: 'Base Estimation',
        description: 'A scientifically derived regression equation that is generally more accurate than 220-Age for a wider population.',
        audience: 'General fitness enthusiasts and older adults.',
        formula: 'Max HR = 208 - (0.7 × Age)',
        accuracyBg: 'bg-blue-50 text-blue-800'
      };
    case 'fox':
      return {
        name: 'Fox & Haskell',
        accuracy: 'Low / Discouraged ⛔',
        description: 'The oldest and simplest formula. Often inaccurate, with a standard deviation of ~10-12 bpm.',
        audience: 'Rough estimation only. Not recommended for serious training.',
        formula: 'Max HR = 220 - Age',
        accuracyBg: 'bg-red-50 text-red-800'
      };
    default:
      return {};
  }
};

const Home = () => {
  const [method, setMethod] = useState('tanaka');
  const [sport, setSport] = useState('running');
  const [formData, setFormData] = useState({
    age: '',
    restingHR: '',
    maxHR: '',
    lthr: '',
    vt1: '',
    vt2: ''
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value)
    }));
    setError('');
    setResult(null);
  };

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setResult(null);
    setError('');
  };

  const handleSportChange = (newSport) => {
    setSport(newSport);
    setResult(null);
    setError('');
  };

  const handleCalculate = () => {
    setError('');
    
    const data = {
      method,
      sport,
      age: formData.age || undefined,
      restingHR: formData.restingHR || undefined,
      maxHR: formData.maxHR || undefined,
      lthr: formData.lthr || undefined,
      vt1: formData.vt1 || undefined,
      vt2: formData.vt2 || undefined
    };

    const zonesResult = calculateZones(data);
    
    if (!zonesResult) {
      setError('Dati insufficienti per il metodo selezionato. Controlla i campi obbligatori.');
      setResult(null);
      return;
    }

    setResult(zonesResult);
  };

  const currentMethodInfo = getMethodDisplayInfo(method, sport);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12 text-center space-y-4"
      >
        <h1 className="text-4xl font-light tracking-tight text-gray-900">Training Zones</h1>
        <p className="text-gray-500 max-w-2xl mx-auto font-light text-lg">
          Precision heart rate calculation using physiological standards.
        </p>
      </motion.div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-12">
        <div className="w-full">
          <div className="flex flex-wrap gap-1 bg-gray-50/50 border-b border-gray-100 p-1">
            <button
              className={`flex-1 min-w-[100px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                method === 'lab'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => handleMethodChange('lab')}
            >
              Lab Test 🥇
            </button>
            <button
              className={`flex-1 min-w-[100px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                method === 'friel'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => handleMethodChange('friel')}
            >
              Joe Friel 🥈
            </button>
            <button
              className={`flex-1 min-w-[100px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                method === 'karvonen'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => handleMethodChange('karvonen')}
            >
              Karvonen 🥉
            </button>
            <button
              className={`flex-1 min-w-[100px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                method === 'tanaka'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => handleMethodChange('tanaka')}
            >
              Tanaka
            </button>
            <button
              className={`flex-1 min-w-[100px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                method === 'fox'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => handleMethodChange('fox')}
            >
              Fox & Haskell ⛔
            </button>
          </div>

          <div className="p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={method}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
              >
                {/* Method Info */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 mb-8 space-y-6">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Method</h3>
                    <p className="text-lg text-gray-700">{currentMethodInfo.name}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Accuracy Level</h3>
                    <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${currentMethodInfo.accuracyBg}`}>
                      {currentMethodInfo.accuracy}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Best For</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {currentMethodInfo.audience}
                    </p>
                  </div>
                </div>

                {/* Input Fields */}
                <div className="space-y-6">
                  {method === 'lab' && (
                    <>
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-500">
                            Enter your threshold values from a metabolic cart test or lactate test.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="vt1" className="text-sm font-medium text-gray-700">VT1 / LT1 (Aerobic Threshold)</label>
                        <input
                          id="vt1"
                          name="vt1"
                          type="number"
                          placeholder="e.g., 145 bpm"
                          value={formData.vt1}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="vt2" className="text-sm font-medium text-gray-700">VT2 / LT2 (Anaerobic Threshold)</label>
                        <input
                          id="vt2"
                          name="vt2"
                          type="number"
                          placeholder="e.g., 172 bpm"
                          value={formData.vt2}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                    </>
                  )}

                  {method === 'friel' && (
                    <>
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-500">
                            Enter your average heart rate from the last 20 minutes of a hard 30-minute solo time trial.
                          </p>
                        </div>
                        
                        {/* Sport Selector for Friel */}
                        <div className="flex gap-4 pt-2">
                          <button
                            type="button"
                            onClick={() => handleSportChange('running')}
                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                              sport === 'running'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Footprints className="w-5 h-5" />
                            <span className="font-medium">Running</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSportChange('cycling')}
                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                              sport === 'cycling'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Bike className="w-5 h-5" />
                            <span className="font-medium">Cycling</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="lthr" className="text-sm font-medium text-gray-700">Lactate Threshold HR (LTHR)</label>
                        <input
                          id="lthr"
                          name="lthr"
                          type="number"
                          placeholder="e.g., 168 bpm"
                          value={formData.lthr}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                    </>
                  )}

                  {method === 'karvonen' && (
                    <>
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-500">
                            Requires both your Maximum Heart Rate (measured, not estimated) and your Resting Heart Rate.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="maxHR" className="text-sm font-medium text-gray-700">Maximum Heart Rate</label>
                        <input
                          id="maxHR"
                          name="maxHR"
                          type="number"
                          placeholder="e.g., 190 bpm"
                          value={formData.maxHR}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="restingHR" className="text-sm font-medium text-gray-700">Resting Heart Rate</label>
                        <input
                          id="restingHR"
                          name="restingHR"
                          type="number"
                          placeholder="e.g., 55 bpm"
                          value={formData.restingHR}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                    </>
                  )}

                  {(method === 'tanaka' || method === 'fox') && (
                    <>
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-500">
                            Estimates maximum heart rate based on age. {method === 'fox' ? 'This method is considered outdated.' : 'This regression equation is generally more accurate.'}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="age" className="text-sm font-medium text-gray-700">Age</label>
                        <input
                          id="age"
                          name="age"
                          type="number"
                          placeholder="e.g., 30"
                          value={formData.age}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-gray-400 focus:ring-gray-400"
                        />
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="bg-red-50 text-red-800 p-4 rounded-lg border-l-4 border-red-800 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleCalculate}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white h-12 rounded-lg font-medium mt-4 transition-colors"
                  >
                    Calculate Zones
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between px-2">
              <h2 className="text-2xl font-light text-gray-900">Your Zones</h2>
              {result.calculatedMaxHR && (
                <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">
                  Est. Max HR: {result.calculatedMaxHR} bpm
                </span>
              )}
            </div>

            {result.info?.zoneCalculation && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
                <Calculator className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Zone Calculation</h4>
                  <p className="text-sm font-medium text-gray-900 font-mono">
                    {result.info.zoneCalculation}
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-4">
              {result.zones.map((zone, index) => (
                <ZoneCard
                  key={zone.number}
                  zone={zone}
                  index={index}
                  totalZones={result.zones.length}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;

